import {
    ITooltipParams,
    ProcessCellForExportParams,
    ValueFormatterParams,
} from 'ag-grid-community';
import {
    BudgetAtterrissageOrPluriannual,
    BudgetExpenseOrIncome,
    BudgetSections,
} from 'business/constants';
import { t } from 'i18next';
import { ExcelExport } from './GridApiContext';
import { getSectionLabel } from 'routes/BudgetModule/components/BudgetOverview/AllProjections/Components/helpers';
import { BUDGET_BALANCE_TABLES } from 'routes/BudgetModule/components/BudgetOverview/BudgetSynthesisAndRatios/BudgetBalance/constants';
import { SecondaryBudgetAxisSources } from 'routes/Support/components/ClientSupport/components/BudgetConfiguration/types';

const translateNamePart = (s: string) => {
    switch (s) {
        case 'expense':
            return t(`budget.${s}`);
        case 'income':
            return t(`budget.${s}`);
        case 'pluriannual':
            return t('projection.pluriannualProjection');
        case 'atterrissage':
            return t('projection.atterrissageProjection');
        default:
            return t(s);
    }
};
/**
 * Returns a filename for Excel file (.xlsx)
 * @param modulePrefix prefix for filename
 * @param extra list of suffixes
 */

export const getExcelFilename = (modulePrefix: string, extra?: string[]) => {
    const translatedPrefix = t(modulePrefix);
    if (!extra || extra.length === 0) return `${t(translatedPrefix)}.xlsx`;
    const suffixes = extra
        .filter(Boolean)
        .map((s) => translateNamePart(s.toLocaleLowerCase()))
        .join(' - ');
    return `${t(translatedPrefix)} - ${suffixes}.xlsx`;
};

/**
 * Get the label for the expense or income
 * @param expenseIncome
 * @returns the translated label
 */
export const getExpenseIncomeLabel = (
    expenseIncome: BudgetExpenseOrIncome,
): string | never => {
    if (expenseIncome === BudgetExpenseOrIncome.EXPENSE) {
        return t('budget.expense');
    }
    if (expenseIncome === BudgetExpenseOrIncome.INCOME) {
        return t('budget.income');
    }
    throw new Error('Invalid expenseIncome');
};

export const ALL_OPERATION_FILE_PREFIX =
    'budgetOverview.allSecondaryBudgetAxis';
export const BUDGET_BALANCE_PREFIX = 'budgetOverview.budgetBalance';
export const ALL_INSCRIPTION_FILE_PREFIX = 'budgetOverview.allInscriptions';
const ALL_OPERATION_EXPORTED_SHEETS = [
    'budgetOverview.financeSecondaryBudgetAxisElement',
    'budgetOverview.mantySecondaryBudgetAxisElement',
];
export const ALL_OPERATION_INSCRIPTIONS_FILE_PREFIX =
    'budgetOverview.allProjectionInscriptions';
const ALL_OPERATION_INSCRIPTIONS_EXPORTED_SHEETS = [
    'budgetOverview.allInscriptions',
];
const ALL_INSCRIPTION_EXPORTED_SHEETS = ['budgetOverview.allInscriptions'];
export const ALL_PROJECTION_FILE_PREFIX = 'budgetOverview.allProjections';
export const PROJECTION_FILE_PREFIX = 'budget.projection';
export const MULTI_YEAR_COMPARISON_PREFIX =
    'budgetOverview.multiYearsComparison';
export const ALL_AP_FILE_PREFIX = 'budgetOverview.allAps';
const ALL_PROJECTION_EXPORTED_SHEETS = [
    getSectionLabel(BudgetSections.OPERATING, t),
    getSectionLabel(BudgetSections.INVESTMENT, t),
    getSectionLabel(null, t),
];
const PROJECTION_EXPORTED_SHEETS = [
    getExpenseIncomeLabel(BudgetExpenseOrIncome.EXPENSE),
    getExpenseIncomeLabel(BudgetExpenseOrIncome.INCOME),
];

export const DEFAULT_GRID_EXPORT_STATE = (
    sourceTable: SecondaryBudgetAxisSources | null,
): {
    [fileName: string]: ExcelExport;
} => ({
    [getExcelFilename(BUDGET_BALANCE_PREFIX, [])]: {
        sheets: [],
        sheetOrder: BUDGET_BALANCE_TABLES.map((ta) => t(ta.title)),
    },
    [getExcelFilename(`${ALL_OPERATION_FILE_PREFIX}_${sourceTable}`, [
        BudgetExpenseOrIncome.EXPENSE,
    ])]: { sheets: [], sheetOrder: ALL_OPERATION_EXPORTED_SHEETS },
    [getExcelFilename(`${ALL_OPERATION_FILE_PREFIX}_${sourceTable}`, [
        BudgetExpenseOrIncome.INCOME,
    ])]: { sheets: [], sheetOrder: ALL_OPERATION_EXPORTED_SHEETS },
    [getExcelFilename(ALL_INSCRIPTION_FILE_PREFIX, [
        BudgetExpenseOrIncome.EXPENSE,
    ])]: { sheets: [], sheetOrder: ALL_INSCRIPTION_EXPORTED_SHEETS },
    [getExcelFilename(ALL_INSCRIPTION_FILE_PREFIX, [
        BudgetExpenseOrIncome.INCOME,
    ])]: { sheets: [], sheetOrder: ALL_INSCRIPTION_EXPORTED_SHEETS },
    [getExcelFilename(ALL_PROJECTION_FILE_PREFIX, [
        BudgetExpenseOrIncome.EXPENSE,
        BudgetAtterrissageOrPluriannual.ATTERRISSAGE,
    ])]: { sheets: [], sheetOrder: ALL_PROJECTION_EXPORTED_SHEETS },
    [getExcelFilename(ALL_OPERATION_INSCRIPTIONS_FILE_PREFIX, [
        BudgetExpenseOrIncome.INCOME,
    ])]: { sheets: [], sheetOrder: ALL_OPERATION_INSCRIPTIONS_EXPORTED_SHEETS },
    [getExcelFilename(ALL_OPERATION_INSCRIPTIONS_FILE_PREFIX, [
        BudgetExpenseOrIncome.EXPENSE,
    ])]: { sheets: [], sheetOrder: ALL_OPERATION_INSCRIPTIONS_EXPORTED_SHEETS },
    [getExcelFilename(ALL_PROJECTION_FILE_PREFIX, [
        BudgetExpenseOrIncome.EXPENSE,
        BudgetAtterrissageOrPluriannual.PLURIANNUAL,
    ])]: { sheets: [], sheetOrder: ALL_PROJECTION_EXPORTED_SHEETS },
    [getExcelFilename(ALL_PROJECTION_FILE_PREFIX, [
        BudgetExpenseOrIncome.INCOME,
        BudgetAtterrissageOrPluriannual.ATTERRISSAGE,
    ])]: { sheets: [], sheetOrder: ALL_PROJECTION_EXPORTED_SHEETS },
    [getExcelFilename(ALL_PROJECTION_FILE_PREFIX, [
        BudgetExpenseOrIncome.INCOME,
        BudgetAtterrissageOrPluriannual.PLURIANNUAL,
    ])]: { sheets: [], sheetOrder: ALL_PROJECTION_EXPORTED_SHEETS },
    [getExcelFilename(PROJECTION_FILE_PREFIX, [
        BudgetAtterrissageOrPluriannual.ATTERRISSAGE,
    ])]: { sheets: [], sheetOrder: PROJECTION_EXPORTED_SHEETS },
    [getExcelFilename(PROJECTION_FILE_PREFIX, [
        BudgetAtterrissageOrPluriannual.PLURIANNUAL,
    ])]: { sheets: [], sheetOrder: PROJECTION_EXPORTED_SHEETS },
    [getExcelFilename(MULTI_YEAR_COMPARISON_PREFIX)]: {
        sheets: [],
        sheetOrder: [MULTI_YEAR_COMPARISON_PREFIX],
    },
    [getExcelFilename(ALL_AP_FILE_PREFIX, [BudgetExpenseOrIncome.EXPENSE])]: {
        sheets: [],
        sheetOrder: ['APs', 'AEs'],
    },
    [getExcelFilename(ALL_AP_FILE_PREFIX, [BudgetExpenseOrIncome.INCOME])]: {
        sheets: [],
        sheetOrder: ['APs', 'AEs'],
    },
});

// TODO: Explo trouver une solution moins hacky ShortcutID : 36985
/*
    Callback to process ag-grid cell for export, retrieving cell value from either
    [valueFormatter, tooltipValueGetter, params.value].

*/

const processCell = (params: ProcessCellForExportParams) => {
    const colDef = params.column.getColDef();

    if (colDef.context?.excelValueFormatter) {
        return colDef.context.excelValueFormatter(params);
    }
    // the check for useValueFormatterForExport must be against "false" because it is undefined when we
    // fetch the colDef from getColDef, despite the default value being "true" according to ag-grid documentation
    if (colDef.valueFormatter && colDef.useValueFormatterForExport !== false) {
        const genericParams: ValueFormatterParams = {
            ...params,
            data: params?.node?.data,
            node: params.node!,
            colDef: params.column.getColDef(),
        };
        return typeof colDef.valueFormatter !== 'string'
            ? colDef.valueFormatter(genericParams)
            : colDef.valueFormatter;
    }
    // if a column is grouped, even if valueFormatter is defined, it will be replaced by tooltipValueGetter
    if (colDef.tooltipValueGetter) {
        const genericParams: ITooltipParams = {
            ...params,
            data: params?.node?.data,
            node: params.node!,
            colDef: params.column.getColDef(),
            location: 'UNKNOWN',
        };
        const tooltipValue = colDef.tooltipValueGetter(genericParams);
        return tooltipValue ? tooltipValue : params.value;
    }
    return params.value;
};

/*
    Exports selected excel file with all its sheets according to gridApis.

    excelExport: all grids to be exported with their sheetnames
    fileName: name of the file to be exported
*/
export const exportGridsToExcel = (
    gridApis: ExcelExport,
    fileName: string,
    sourceTable?: string,
) => {
    const translatedSheetOrder = gridApis.sheetOrder.map((sheet) =>
        sourceTable ? t(sheet, { sourceTable }) : t(sheet),
    );
    gridApis.sheets.sort((a, b) => {
        const indexA = translatedSheetOrder.indexOf(a.sheetName);
        const indexB = translatedSheetOrder.indexOf(b.sheetName);
        if (indexA > indexB) {
            return 1;
        }
        if (indexA < indexB) {
            return -1;
        }
        return 0;
    });

    const spreadsheets = gridApis.sheets.reduce(
        (accumulator: string[], currentValue) => {
            accumulator.push(
                currentValue.grid.getSheetDataForExcel({
                    sheetName: currentValue.sheetName,
                    ...currentValue.exportOptions,
                    processCellCallback: processCell,
                })!,
            );
            return accumulator;
        },
        [],
    );
    gridApis.sheets[0].grid.exportMultipleSheetsAsExcel({
        data: spreadsheets,
        fileName,
    });
};
